import ChartLoading from '../../../components/ChartLoading/ChartLoading';
import React, { useCallback, useMemo, useState } from 'react';
import { Button, ButtonGroup, Checkbox, FormControlLabel } from '@attain-sre/attain-design-system';
import { useTooltip } from '@nivo/tooltip';
import { GET_TTD_OTS_SEGMENT_METRICS } from '../../../graphql/queries';
import { NIVO_COMMON, NIVO_COMMON_LINE } from '../utils/nivoCommon';
import {
  OTS_SGM_MNG_AVG_RELEVANCE_PCTL,
  OTS_SGM_MNG_AVG_VALUE_PCTL,
  OTS_SGM_MNG_CHART_DATA_NOT_AVAILABLE,
  OTS_SGM_MNG_MATCH_PCT_PCTL,
  OTS_SGM_MNG_MONTHLY,
  OTS_SGM_MNG_PAID_PCT_PCTL,
  OTS_SGM_MNG_QUARTERLY,
  OTS_SGM_MNG_TTD_PERFORMANCE_HEADING,
  OTS_SGM_MNG_TTD_PERFORMANCE_SUBTITLE,
  OTS_SGM_MNG_TTD_PERFORMANCE_SUBTITLE_QUARTERLY,
  OTS_SGM_MNG_TTD_PERFORMANCE_SUBTITLE_WEEKLY,
  OTS_SGM_MNG_WEEKLY,
} from '../../../content';
import { PeriodData, SegmentSizeRevenueResponse } from '../types';
import { RateLevel } from '../../../constants';
import { ResponsiveLine } from '@nivo/line';
import { buildSegmentQueryVariables } from '../utils/urlHelpers';
import { useQuery } from '@apollo/client';
import * as Styled from './index.styles';

const METRIC_KEYS = [
  { id: 'avgRelevance', dataKey: 'avgRelevancePercentile' as const, label: OTS_SGM_MNG_AVG_RELEVANCE_PCTL, color: 'rgb(128, 90, 160)' },
  { id: 'avgValue', dataKey: 'avgValuePercentile' as const, label: OTS_SGM_MNG_AVG_VALUE_PCTL, color: 'rgb(255, 140, 0)' },
  { id: 'matchPct', dataKey: 'matchedPercentile' as const, label: OTS_SGM_MNG_MATCH_PCT_PCTL, color: 'rgb(70, 160, 100)' },
  { id: 'paidPct', dataKey: 'paidPercentile' as const, label: OTS_SGM_MNG_PAID_PCT_PCTL, color: 'rgb(220, 80, 80)' },
] as const;

interface SegmentTtdPerformanceChartProps {
  segmentId: string;
  rateLevel?: RateLevel;
  advertiserId?: string;
  partnerId?: string;
}

type PeriodType = 'weekly' | 'monthly' | 'quarterly';

function percentileToChartY(
  pct: { value?: number | string | null } | null | undefined
): number {
  if (pct == null || pct.value == null || pct.value === '') {
    return 0;
  }
  const n = Number(pct.value);
  return Number.isFinite(n) ? n : 0;
}

export const SegmentTtdPerformanceChart: React.FC<
  SegmentTtdPerformanceChartProps
> = ({ segmentId, rateLevel = RateLevel.SYSTEM, advertiserId, partnerId }) => {
  const [periodType, setPeriodType] = useState<PeriodType>('weekly');
  const [visibleMetrics, setVisibleMetrics] = useState<Record<string, boolean>>({
    avgRelevance: true,
    avgValue: true,
    matchPct: true,
    paidPct: true,
  });

  const queryVariables = useMemo(
    () =>
      buildSegmentQueryVariables(
        segmentId,
        rateLevel,
        advertiserId,
        partnerId
      ),
    [segmentId, rateLevel, advertiserId, partnerId]
  );

  const { data, loading, error } = useQuery<{
    ttdOtsSegmentMetrics: SegmentSizeRevenueResponse;
  }>(GET_TTD_OTS_SEGMENT_METRICS, {
    variables: {
      ...queryVariables,
      periodType,
      month: undefined,
    },
    fetchPolicy: 'network-only',
    skip: !segmentId,
  });

  const chartData = data?.ttdOtsSegmentMetrics;

  const { lineData } = useMemo(() => {
    if (!chartData?.periods?.length) {
      return { lineData: [] };
    }

    const dataMap = new Map<string, PeriodData>();
    (chartData.data ?? []).forEach((d) => dataMap.set(d.period, d));

    // Plot missing/null as 0 so each series draws a full line along 0% (same as revenue chart)
    const series = METRIC_KEYS.filter((m) => visibleMetrics[m.id]).map((metric) => {
      const points = chartData.periods.map((period) => {
        const point = dataMap.get(period);
        const pct = point?.[metric.dataKey];
        const value = percentileToChartY(pct);
        return { x: period, y: value };
      });
      return {
        id: metric.label,
        color: metric.color,
        data: points,
      };
    });

    return { lineData: series };
  }, [chartData, visibleMetrics]);

  // Tooltip for any period (row may be missing when API only returns months with data)
  const renderTooltipContent = useCallback(
    (period: string) => {
      const point = chartData?.data.find((d) => d.period === period);
      return (
        <Styled.TooltipContent>
          <Styled.TooltipTitle>{period}</Styled.TooltipTitle>
          {METRIC_KEYS.map((m) => {
            const pct = point?.[m.dataKey];
            const value = pct?.label ?? 'N/A';
            return (
              <Styled.TooltipRow key={m.id}>
                <Styled.TooltipLabel>{m.label}:</Styled.TooltipLabel>
                <Styled.TooltipValue>{value}</Styled.TooltipValue>
              </Styled.TooltipRow>
            );
          })}
        </Styled.TooltipContent>
      );
    },
    [chartData]
  );

  /**
   * Full-width bands per x tick so tooltips work when only one month has points.
   * useMesh + Voronoi fails when all nodes share one x (collinear); Nivo x-slices
   * only exist for ticks that have points, so a single month left almost no hit area.
   */
  const PeriodBandLayer = useMemo(() => {
    const periods = chartData?.periods ?? [];
    if (periods.length === 0) return () => null;
    const BandLayer: React.FC<{
      innerWidth: number;
      innerHeight: number;
      xScale: (v: string) => number;
    }> = ({ innerWidth, innerHeight, xScale }) => {
      const { showTooltipFromEvent, hideTooltip } = useTooltip();
      return (
        <g>
          {periods.map((period, i) => {
            const xc = xScale(period);
            const x0 =
              i === 0 ? 0 : (xScale(periods[i - 1]!) + xc) / 2;
            const x1 =
              i === periods.length - 1
                ? innerWidth
                : (xc + xScale(periods[i + 1]!)) / 2;
            return (
              <rect
                key={period}
                x={x0}
                y={0}
                width={Math.max(1, x1 - x0)}
                height={innerHeight}
                fill="transparent"
                style={{ cursor: 'crosshair' }}
                onMouseEnter={(e) =>
                  showTooltipFromEvent(renderTooltipContent(period), e, 'right')
                }
                onMouseMove={(e) =>
                  showTooltipFromEvent(renderTooltipContent(period), e, 'right')
                }
                onMouseLeave={hideTooltip}
              />
            );
          })}
        </g>
      );
    };
    return BandLayer;
  }, [chartData?.periods, renderTooltipContent]);

  const handleMetricToggle = (id: string) => {
    setVisibleMetrics((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (error) {
    return null;
  }

  return (
    <Styled.CardContainer>
      <Styled.HeaderContainer>
        <Styled.TitleContainer>
          <Styled.Title>{OTS_SGM_MNG_TTD_PERFORMANCE_HEADING}</Styled.Title>
          <Styled.Subtitle>
            {periodType === 'weekly'
              ? OTS_SGM_MNG_TTD_PERFORMANCE_SUBTITLE_WEEKLY
              : periodType === 'quarterly'
              ? OTS_SGM_MNG_TTD_PERFORMANCE_SUBTITLE_QUARTERLY
              : OTS_SGM_MNG_TTD_PERFORMANCE_SUBTITLE}
          </Styled.Subtitle>
        </Styled.TitleContainer>
        <ButtonGroup>
          <Button
            onClick={() => setPeriodType('weekly')}
            variant={periodType === 'weekly' ? 'contained' : 'outlined'}
          >
            {OTS_SGM_MNG_WEEKLY}
          </Button>
          <Button
            onClick={() => setPeriodType('monthly')}
            variant={periodType === 'monthly' ? 'contained' : 'outlined'}
          >
            {OTS_SGM_MNG_MONTHLY}
          </Button>
          <Button
            onClick={() => setPeriodType('quarterly')}
            variant={periodType === 'quarterly' ? 'contained' : 'outlined'}
          >
            {OTS_SGM_MNG_QUARTERLY}
          </Button>
        </ButtonGroup>
      </Styled.HeaderContainer>

      <Styled.CheckboxRow>
        {METRIC_KEYS.map((m) => (
          <FormControlLabel
            key={m.id}
            control={
              <Checkbox
                checked={!!visibleMetrics[m.id]}
                onChange={() => handleMetricToggle(m.id)}
                size="small"
              />
            }
            label={m.label}
          />
        ))}
      </Styled.CheckboxRow>

      <Styled.ContentWrapper>
        {loading ? (
          <Styled.ChartContainer>
            <ChartLoading barOrientation="vertical" chartType="bar" />
          </Styled.ChartContainer>
        ) : chartData && lineData.length > 0 ? (
          <Styled.ChartContainer>
            <div style={{ height: '300px' }}>
              <ResponsiveLine
                {...NIVO_COMMON_LINE}
                enableArea={false}
                animate={true}
                data={lineData}
                margin={{ top: 13, right: 50, bottom: 31, left: 55 }}
                xScale={{ type: 'point' }}
                yScale={{
                  type: 'linear',
                  min: 0,
                  max: 100,
                }}
                axisTop={null}
                axisRight={null}
                axisLeft={{
                  ...NIVO_COMMON.axes,
                  format: (v) => `${v}%`,
                  legend: '',
                  legendOffset: -45,
                  tickValues: [0, 25, 50, 75, 100],
                }}
                axisBottom={{
                  ...NIVO_COMMON.axes,
                }}
                enableGridY={false}
                enableSlices={false}
                useMesh={false}
                layers={[
                  'grid',
                  'markers',
                  'axes',
                  'lines',
                  'points',
                  PeriodBandLayer,
                  'legends',
                ]}
                colors={(d) => d.color}
                tooltip={({ point }) =>
                  renderTooltipContent(String(point?.data?.x ?? ''))
                }
              />
            </div>

            <Styled.LegendContainer>
              {METRIC_KEYS.filter((m) => visibleMetrics[m.id]).map((m) => (
                <Styled.LegendItem key={m.id}>
                  <Styled.LegendDot $color={m.color} />
                  <Styled.LegendLabel>{m.label}</Styled.LegendLabel>
                </Styled.LegendItem>
              ))}
            </Styled.LegendContainer>
          </Styled.ChartContainer>
        ) : (
          <Styled.NoDataContainer>
            <Styled.NoDataText>
              {OTS_SGM_MNG_CHART_DATA_NOT_AVAILABLE}
            </Styled.NoDataText>
          </Styled.NoDataContainer>
        )}
      </Styled.ContentWrapper>
    </Styled.CardContainer>
  );
};

export default SegmentTtdPerformanceChart;
